import { useSuspenseQuery } from '@tanstack/react-query'
import {
  Link,
  type LinkOptions,
  Outlet,
  createFileRoute,
  linkOptions,
  useLocation,
} from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { EditIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@shadcn/breadcrumb'
import { Button } from '@shadcn/button'

import { queryCompanyGet } from '@api/company/company.queries'

import DisplayCurrencySelect from '@/components/currency/display-currency-select'
import {
  EntityDescription,
  EntityDescriptionItem,
  EntityDescriptionSeparator,
  EntityDescriptionUsersItem,
} from '@/components/entity-description'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatLastUpdate } from '@/lib/date'
import { pageSearchAtom } from '@/stores/page-search.atom'

import { Route as CompaniesRoute } from '../../index'

export const Route = createFileRoute('/app/data/companies/$uuid/(mainPage)')({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const { uuid } = Route.useParams()
  const { data } = useSuspenseQuery(queryCompanyGet(uuid))
  const search = useAtomValue(pageSearchAtom)
  const backLinkOptions =
    search[CompaniesRoute.fullPath] ??
    linkOptions({ to: '/app/data/companies' })

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-hidden">
      <div className="flex w-full flex-col justify-between gap-4 p-4 pb-0 sm:flex-row sm:items-center sm:gap-0">
        <div className="flex flex-col gap-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink {...backLinkOptions}>
                  {t('data.companies.uuid.breadcrumb')}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {data.commonFields.legalName ?? 'Ø'}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-bold">
              {data.commonFields.legalName ?? 'Ø'}
            </h1>
            <EntityDescription>
              <EntityDescriptionItem
                label={t('data.companies.uuid.subtitle.siren')}
                value={data.identifiers.identifierSiren}
              />
              <EntityDescriptionSeparator />
              <EntityDescriptionItem
                label={t('data.companies.uuid.subtitle.vat')}
                value={data.identifiers.identifierVat}
              />
              <EntityDescriptionSeparator />
              <EntityDescriptionItem
                label={t('data.companies.uuid.subtitle.last-update')}
                value={formatLastUpdate(data.updatedAt)}
              />
              <EntityDescriptionUsersItem users={data.users} />
            </EntityDescription>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SwitchViewTabs companyUuid={uuid} />
          <DisplayCurrencySelect className="h-8" />
          <Route.Link
            params={{ uuid }}
            viewTransition
            to="/app/data/companies/$uuid/edit"
          >
            <Button
              variant="outlineGray"
              className="size-8 rounded-sm"
              size="ghost"
              type="button"
            >
              <EditIcon className="size-4" />
            </Button>
          </Route.Link>
        </div>
      </div>
      <Outlet />
    </div>
  )
}

const subroutes: string[] = ['files', 'insistences', 'policies', 'coverages']

interface SwitchViewTabsProps {
  companyUuid: string
}

/**
 * Stay on the matching page for other view when switching.
 *
 * e.g. /debtor/files -> /insured/files
 */
const SwitchViewTabs = ({ companyUuid }: SwitchViewTabsProps) => {
  const location = useLocation()
  const value = location.pathname.split('/').pop()
  const parsedValue = value && subroutes.includes(value) ? value : '.'
  const tabsValue = location.pathname.includes('insured') ? 'insured' : 'debtor'
  const { data: company } = useSuspenseQuery(queryCompanyGet(companyUuid))

  return (
    <Tabs value={tabsValue}>
      <TabsList>
        {(
          [
            { label: 'insured', enabled: company.stats.isInsured },
            { label: 'debtor', enabled: company.stats.isDebtor },
          ] as const
        )
          // Just don't show the tab if the company is not of this type
          .filter(({ enabled }) => enabled)
          .map(({ label, enabled }) => (
            <Link
              key={label}
              {...pathMatches[parsedValue][label]}
              viewTransition
              activeOptions={{ exact: false, includeSearch: false }}
              disabled={!enabled}
            >
              <TabsTrigger disabled={!enabled} value={label}>
                {label}
              </TabsTrigger>
            </Link>
          ))}
      </TabsList>
    </Tabs>
  )
}
const pathMatches: Record<string, Record<string, LinkOptions>> = {
  '.': {
    debtor: linkOptions({
      from: Route.fullPath,
      to: '/app/data/companies/$uuid/debtor',
    }),
    insured: linkOptions({
      from: Route.fullPath,
      to: '/app/data/companies/$uuid/insured',
    }),
  },
  files: {
    debtor: linkOptions({
      from: Route.fullPath,
      to: '/app/data/companies/$uuid/debtor/files',
    }),
    insured: linkOptions({
      from: Route.fullPath,
      to: '/app/data/companies/$uuid/insured/files',
    }),
  },
  policies: {
    insured: linkOptions({
      from: Route.fullPath,
      to: '/app/data/companies/$uuid/insured/policies',
    }),
  },
  coverages: {
    debtor: linkOptions({
      from: Route.fullPath,
      to: '/app/data/companies/$uuid/debtor/coverages',
    }),
    insured: linkOptions({
      from: Route.fullPath,
      to: '/app/data/companies/$uuid/insured/coverages',
    }),
  },
  insistences: {
    debtor: linkOptions({
      from: Route.fullPath,
      to: '/app/data/companies/$uuid/debtor/insistences',
    }),
    insured: linkOptions({
      from: Route.fullPath,
      to: '/app/data/companies/$uuid/insured/insistences',
    }),
  },
}
